import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:3001') + '/api';

// Shares the "orders needing shipping" count with the nav so it can show a
// badge on the Orders tab, drawing people toward buying labels even when
// they're not on the Orders page. Backed by a cheap /orders/counts query.
const ShippingCountContext = createContext({ needsShipping: 0, pendingOffers: 0, refresh: () => {} });

export function ShippingCountProvider({ children }) {
  const { user } = useAuth();
  const [needsShipping, setNeedsShipping] = useState(0);
  const [pendingOffers, setPendingOffers] = useState(0);

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API}/orders/counts`);
      setNeedsShipping(Number(res.data?.needsShipping) || 0);
      setPendingOffers(Number(res.data?.pendingOffers) || 0);
    } catch (e) { /* nav badge is best-effort; ignore failures */ }
  }, [user]);

  // Fetch on login/mount, when the tab regains focus, and whenever Orders
  // signals a change (record sale, buy label, cancel, refund, import).
  useEffect(() => {
    if (!user) { setNeedsShipping(0); return; }
    refresh();
    const onFocus = () => refresh();
    const onChange = () => refresh();
    window.addEventListener('focus', onFocus);
    window.addEventListener('scn-orders-changed', onChange);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('scn-orders-changed', onChange);
    };
  }, [user, refresh]);

  return (
    <ShippingCountContext.Provider value={{ needsShipping, pendingOffers, refresh }}>
      {children}
    </ShippingCountContext.Provider>
  );
}

export function useShippingCount() {
  return useContext(ShippingCountContext);
}
